import type { PanelItem } from '../../../panel-items/panel-item.class';

/**
 * Fridge trim kits are generated by the rules engine on `panel_items.add` of
 * the fridge and deleted with it — but they are created through `newFromSku`,
 * so `ruleManaged` is false and the only way to tell them apart from a part the
 * customer added is the SKU. Keep in sync with `rules-engine/rules/fridge-trim-kit`.
 */
export const TRIM_KIT_SKUS: ReadonlySet<string> = new Set(['C115TK', 'C121TK', 'C124TK', 'CBIR21TK', 'CBIR24TK', 'CRTA21TK']);

/** Keg taps are re-created by `requires-kegtap`. Keep in sync with that rule. */
export const KEG_TAP_SKU = 'C124KEG-TAP';

/**
 * A column member the rules engine owns and will re-create at the destination on its own: jackets,
 * keg taps, vents, passthroughs and trim kits. Re-adding one of these explicitly duplicates it — the
 * memory note "library-generated parts" — so a move carries only the other items and lets the rules
 * run on the re-add of their anchor.
 *
 * Deliberately not `panelItem.ruleManaged`: saved data written before that field existed has no value
 * for it and PanelItem defaults those to managed, so every item of every legacy design answers true
 * here. That made `hasAppliance` false for every column and left nothing draggable in 3D.
 */
export const isRuleGeneratedCompanionCfn = (panelItem: PanelItem): boolean => {
	const sku = panelItem.item?.sku ?? '';
	if (TRIM_KIT_SKUS.has(sku) || sku === KEG_TAP_SKU) return true;
	const category = panelItem.item?.category;
	return Boolean(category?.isVent || category?.isPassthrough || category?.isInsulatedJacket);
};

/**
 * The appliance a column is "about" for naming and reference purposes: the
 * first member that is not a rule-generated companion, else the first member.
 */
export const getGroupAnchorItemCfn = (items: Array<PanelItem>): PanelItem | null => {
	if (items.length === 0) return null;
	return items.find((pi) => !isRuleGeneratedCompanionCfn(pi)) ?? items[0];
};
